import { AfterViewInit, Directive, EventEmitter, Host, Input, OnDestroy, Optional, Output } from '@angular/core';

import OpenSeadragon from 'openseadragon';
import { DziViewerComponent } from '../../dzi-viewer.component';
import { DziOverlaysViewerDirective, OverlayData } from '../dzi-overlays-viewer/dzi-overlays-viewer.directive';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TOsdActiveAction } from '@telespot/shared/viewers/data-access';

@Directive({
    selector: '[tsDziMouseBindings]',
    standalone: false
})
export class DziMouseBindingsDirective implements AfterViewInit, OnDestroy {
  @Input() overlays: OverlayData[] = [];
  @Input() viewerMode: TOsdActiveAction = TOsdActiveAction.idle;
  @Output() mouseDown = new EventEmitter<MouseEvent>();
  @Output() mouseMove = new EventEmitter<MouseEvent>();
  @Output() mouseUp = new EventEmitter<void>();
  @Output() rightClick = new EventEmitter<MouseEvent>();

  private destroy$ = new Subject<void>();

  constructor(
    @Host() @Optional() private dziViewer: DziViewerComponent,
    @Host() private overlaysRenderer: DziOverlaysViewerDirective
  ) {}

  ngAfterViewInit(): void {
    if (!this.dziViewer) return;

    this.overlaysRenderer.overlaysRendered.pipe(takeUntil(this.destroy$)).subscribe(() => this.addOverlaysHandlers());

    this.setupCanvasMouseTracker(); // central mouse button
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isMovementModeActive(): boolean {
    return this.viewerMode === TOsdActiveAction.idle;
  }

  private setupCanvasMouseTracker(): void {
    const canvas = this.dziViewer['viewer'].canvas;

    canvas.addEventListener(
      'contextmenu',
      (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      },
      { capture: true, passive: false }
    );

    new OpenSeadragon.MouseTracker({
      element: canvas,

      nonPrimaryPressHandler: (event: any) => {
        if (event.button === 1) {
          this.mouseDown.emit(event.originalEvent);
        } else if (event.button === 2) {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          event.originalEvent.stopImmediatePropagation();
          this.rightClick.emit(event.originalEvent);
        }
      },

      nonPrimaryReleaseHandler: (event: any) => {
        if (event.button === 1) {
          this.mouseUp.emit();
        }
      },

      moveHandler: (event: any) => {
        if (!this.isMovementModeActive()) return;
        if (event.buttons === 4) {
          this.mouseMove.emit(event.originalEvent);
        }
      },
    }).setTracking(true);
  }

  private addOverlaysHandlers(): void {
    this.overlays.forEach((overlay) => {
      const element = document.getElementById(overlay.id);
      if (!element) return;

      this.setupOverlayMouseTracker(element); // mouse clicked inside overlay
    });
  }

  private setupOverlayMouseTracker(element: HTMLElement): void {
    new OpenSeadragon.MouseTracker({
      element,

      pressHandler: (event: any) => {
        if (!this.isMovementModeActive()) return;
        this.mouseDown.emit(event.originalEvent);
      },

      releaseHandler: () => {
        if (!this.isMovementModeActive()) return;
        this.mouseUp.emit();
      },

      moveHandler: (event: any) => {
        if (!this.isMovementModeActive()) return;
        this.mouseMove.emit(event.originalEvent);
      },
    }).setTracking(true);
  }
}
